<script setup lang="ts">
import { breakpointsTailwind } from '@vueuse/core';
import { ListFilter, X } from 'lucide-vue-next';

const properties = defineProps<{ disabled?: boolean }>();

const searchResultFiltersStore = useSearchResultFiltersStore();

const isOpen = ref(false);

const triggerReference = useTemplateRef<HTMLButtonElement>('trigger');

watch(isOpen, (opened) => {
  if (!opened) return;
  triggerReference.value?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
});

const breakpoints = useBreakpoints(breakpointsTailwind);
const isMd = breakpoints.greaterOrEqual('md');
</script>

<template>
  <UiSheet
    v-if="!isMd"
    v-model:open="isOpen"
  >
    <UiSheetTrigger as-child>
      <button
        ref="trigger"
        class="relative flex h-10 shrink-0 items-center gap-2 rounded-lg border px-3 transition before:pointer-events-none before:absolute before:inset-border-outset before:rounded-lg before:border-2 before:transition before:content-empty"
        :class="[
          properties.disabled
            ? 'border-gray-400 bg-gray-50 text-gray-500'
            : searchResultFiltersStore.activeFiltersCount
              ? 'border-brand-primary-700 bg-brand-primary-50 text-brand-primary-700'
              : 'border-gray-500 bg-gray-50 text-text-primary-700',
          isOpen ? 'before:border-brand-primary-500' : 'before:border-transparent',
        ]"
        :aria-label="$t('components.results.filters.sheetTitle')"
        :disabled="properties.disabled"
        type="button"
      >
        <ListFilter
          class="h-5 w-5"
          aria-hidden="true"
        />
        <Transition
          enter-active-class="transition duration-200"
          enter-from-class="scale-0 opacity-0"
          enter-to-class="scale-100 opacity-100"
          leave-active-class="transition duration-150"
          leave-from-class="scale-100 opacity-100"
          leave-to-class="scale-0 opacity-0"
        >
          <span
            v-if="searchResultFiltersStore.activeFiltersCount > 0"
            aria-live="polite"
            :aria-label="`${searchResultFiltersStore.activeFiltersCount} ${$t('components.results.filters.activeFilters')}`"
            class="flex h-5 w-5 items-center justify-center rounded-full bg-brand-primary-700 text-xs font-semibold text-text-inverse"
          >
            {{ searchResultFiltersStore.activeFiltersCount }}
          </span>
        </Transition>
      </button>
    </UiSheetTrigger>
    <UiSheetContent
      side="bottom"
      class="flex max-h-sheet flex-col gap-0 p-0"
      :hide-close="true"
    >
      <UiSheetHeader class="h-16.5 flex-row items-center justify-between border-b p-4">
        <UiSheetTitle class="text-xl">{{ $t('components.results.filters.sheetTitle') }}</UiSheetTitle>
        <UiSheetClose :aria-label="$t('components.common.close')">
          <X
            class="h-5 w-5"
            aria-hidden="true"
          />
        </UiSheetClose>
      </UiSheetHeader>
      <ResultsFiltersAllBottomSheetContent v-slot="{ count }">
        <UiSheetClose as-child>
          <UiButton class="flex-1">
            {{ $t('components.results.filters.showResults') }} (<span aria-live="polite">{{ count }}</span
            >)
          </UiButton>
        </UiSheetClose>
      </ResultsFiltersAllBottomSheetContent>
    </UiSheetContent>
  </UiSheet>
  <UiDialog
    v-else
    v-model:open="isOpen"
  >
    <UiDialogTrigger as-child>
      <button
        ref="trigger"
        class="relative flex h-10 shrink-0 items-center gap-2 rounded-lg border px-3 transition before:pointer-events-none before:absolute before:inset-border-outset before:rounded-lg before:border-2 before:transition before:content-empty"
        :class="[
          properties.disabled
            ? 'border-gray-400 bg-gray-50 text-gray-500'
            : searchResultFiltersStore.activeFiltersCount
              ? 'border-brand-primary-700 bg-brand-primary-50 text-brand-primary-700'
              : 'border-gray-500 bg-gray-50 text-text-primary-700',
          isOpen ? 'before:border-brand-primary-500' : 'before:border-transparent',
        ]"
        :disabled="properties.disabled"
        type="button"
      >
        <ListFilter
          class="h-5 w-5"
          aria-hidden="true"
        />
        <span class="text-sm font-normal">{{ $t('components.results.filters.sheetTitle') }}</span>
        <Transition
          enter-active-class="transition duration-200"
          enter-from-class="scale-0 opacity-0"
          enter-to-class="scale-100 opacity-100"
          leave-active-class="transition duration-150"
          leave-from-class="scale-100 opacity-100"
          leave-to-class="scale-0 opacity-0"
        >
          <span
            v-if="searchResultFiltersStore.activeFiltersCount > 0"
            aria-live="polite"
            :aria-label="`${searchResultFiltersStore.activeFiltersCount} ${$t('components.results.filters.activeFilters')}`"
            class="flex h-5 w-5 items-center justify-center rounded-full bg-brand-primary-700 text-xs font-semibold text-text-inverse"
          >
            {{ searchResultFiltersStore.activeFiltersCount }}
          </span>
        </Transition>
      </button>
    </UiDialogTrigger>
    <UiDialogContent
      :hide-close="true"
      class="flex max-h-sheet flex-col gap-0 overflow-hidden p-0"
    >
      <UiDialogHeader class="h-16.5 flex-row items-center justify-between border-b p-4">
        <UiDialogTitle class="text-xl">{{ $t('components.results.filters.sheetTitle') }}</UiDialogTitle>
        <UiDialogClose :aria-label="$t('components.common.close')">
          <X
            class="h-5 w-5"
            aria-hidden="true"
          />
        </UiDialogClose>
      </UiDialogHeader>
      <ResultsFiltersAllBottomSheetContent v-slot="{ count }">
        <UiDialogClose as-child>
          <UiButton class="flex-1">
            {{ $t('components.results.filters.showResults') }} (<span aria-live="polite">{{ count }}</span
            >)
          </UiButton>
        </UiDialogClose>
      </ResultsFiltersAllBottomSheetContent>
    </UiDialogContent>
  </UiDialog>
</template>
